import "../assets/css/vendor-product-forms.css";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  Alert,
  Card,
  Form,
  Button,
  Container,
  Row,
  Col,
  Modal,
  Spinner,
} from "react-bootstrap";
import DiscountFields, { discountPayloadOf } from "../components/DiscountFields";
import { useProductFormGate } from "../components/team/Gated";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { updateRestaurantProduct } from "../actions/restaurantProductsAction.js";
import { fetchSections } from "../actions/sectionAction.js";
import {
  deleteRestaurantProduct,
} from "../actions/restaurantActions.js";
import {
  fetchOptionGroupsRestaurant,
  fetchOptionsRestaurants,
} from "../actions/optionAction.js";
import OptionGroupModal from "../components/Restaurant/OptionGroupModal";
import { format } from "date-fns";
import { apiFetchRestaurantProductDetail } from "../api/index";
import {
  FaCameraRetro,
  FaTrash,
  FaSpellCheck,
  FaInfoCircle,
  FaSquareFull,
  FaStar,
} from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import {
  MdAddAPhoto,
  MdCrop,
  MdCropSquare,
  MdCropLandscape,
} from "react-icons/md";
import { uploadImageToCloudinary as sharedUpload } from "../utilities/imageUpload";
import { toast } from "react-toastify";
import Select from "../components/ui/AppReactSelect";
import { Switch, FormControlLabel } from "@material-ui/core";
import { getUserFromLocalStorage } from "../utilities/user.js";
import MobileProductShootPicker from "../components/MobileProductShootPicker";
import AdditionalImagesStrip from "../components/AdditionalImagesStrip";
import ProductMediaDeleteDialog from "../components/ProductMediaDeleteDialog";
import ProductVideoSection from "../components/ProductVideoSection";
import ArtworkTemplateLink from "../components/ArtworkTemplateLink";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ReactCrop from "react-image-crop";
import { centerCrop, makeAspectCrop } from "react-image-crop";
import { IconButton, Tooltip } from "@material-ui/core";
import VersionHistoryDrawer from "../components/VersionHistoryDrawer";
import { useWorkingRestaurant, WorkingRestaurantField } from "./Components/WorkingRestaurant";
import { productPhotoHint } from "../utilities/productPhotoRules";
import AppSelect from "../components/ui/AppSelect";
import { vendorOwnerIdOf } from "../utilities/vendorScope";

const AUDIT_ENABLED = process.env.REACT_APP_FEATURE_AUDIT_TRAIL === "true";

const useStyles = makeStyles((theme) => ({
  label: {
    fontWeight: "normal",
    fontSize: "12px",
    textTransform: "capitalize",
  },
}));

// Custom font CSS
const customFontsCSS = `
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

.ql-font-montserrat {
  font-family: 'Montserrat', sans-serif;
}

.ql-editor {
  font-size: 0.875em !important;  /* Always small */
}

.ql-editor * {
  font-size: inherit !important;
}

.ql-editor h1 {
  font-size: 1.5em !important;
}

.ql-editor h2 {
  font-size: 1.3em !important;
}

.ql-editor h3 {
  font-size: 1.1em !important;
}
`;

// Add custom fonts to Quill
const Font = ReactQuill.Quill.import("formats/font");
// Font.whitelist = ['montserrat', 'roboto', 'lato', 'poppins', 'sans-serif', 'serif', 'monospace'];
Font.whitelist = ["montserrat", "sans-serif"];
ReactQuill.Quill.register(Font, true);

// Format Pasted Content by default
const handlePaste = (e) => {
  e.preventDefault();
  const text = e.clipboardData.getData("text/plain");
  const sanitizedText = text.replace(/(<([^>]+)>)/gi, ""); // Remove any HTML tags
  document.execCommand(
    "insertHTML",
    false,
    `<span style="font-size: 0.875em;">${sanitizedText}</span>`
  );
};

const EditMenuProductForm = () => {
  const classes = useStyles();
  const { productId } = useParams();
  const user = getUserFromLocalStorage();
  // The SIGNED-IN user. Only a fallback now: options, option groups and the
  // phone-shoot picker belong to the dish's restaurant owner (`ownerId` below).
  const restaurantId = vendorOwnerIdOf(user);
  const isSuperAdmin =
    user?.result?.userType === "LUSUKU STAFF" &&
    user?.result?.user_role === "SUPERUSER";

  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  const [productDetails, setProductDetails] = useState(
    location.state?.productDetails || { optionGroups: [] }
  );

  // The restaurant this dish belongs to: shown, never chosen (WorkingRestaurant.js).
  const { restaurant: workingRestaurant, loading: workingRestaurantLoading } =
    useWorkingRestaurant(productDetails.restaurantId, location.state?.restaurantHint || null);
  const ownerId = workingRestaurant?.ownerId || null;

  const sections = useSelector((state) => state.sections.sections);
  const optionGroups = useSelector((state) => state.options.optionGroups);
  const options = useSelector((state) => state.options.options);

  const [imagePreview, setImagePreview] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isFormatCorrect, setIsFormatCorrect] = useState(false);
  const [isSizeCorrect, setIsSizeCorrect] = useState(false);
  const [isResolutionCorrect, setIsResolutionCorrect] = useState(false);
  const [formErrors, setFormErrors] = useState({ section: "", restaurant: "" });
  const [submitting, setSubmitting] = useState(false);
  const gate = useProductFormGate(submitting || uploading);

  const [selectedOptionGroups, setSelectedOptionGroups] = useState([]);
  // The option-group editor (components/Restaurant/OptionGroupModal) creates
  // when `editingGroup` is null and edits that group otherwise.
  const [showOptionGroupModal, setShowOptionGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // The saved photo (https URL) whose delete control was clicked. Opens
  // ProductMediaDeleteDialog, which removes ticked photos from the dish and
  // from Cloudinary immediately. Unsaved picks (dataURLs) never come here.
  const [photoDeleteTarget, setPhotoDeleteTarget] = useState(null);

  const isSavedPhoto = (url) => typeof url === "string" && /^https?:\/\//i.test(url);

  // Cover "Delete Image": a saved cover goes through the dialog (the update
  // endpoint ignores an empty image, so dropping it locally never reached the
  // server); an unsaved pick is simply discarded.
  const handleDeleteCover = () => {
    if (isSavedPhoto(productDetails?.image)) {
      setPhotoDeleteTarget(productDetails.image);
      return;
    }
    setProductDetails((prev) => ({ ...prev, image: null }));
    setImagePreview(null);
  };

  const handlePhotosRemoved = ({ image, additionalImages }) => {
    setProductDetails((prev) => ({ ...prev, image, additionalImages }));
    if (!image) setImagePreview(null);
  };
  const [showHistory, setShowHistory] = useState(false);

  const commercialRestaurant = workingRestaurant;
  const commercialRate = Number(
    commercialRestaurant?.commissionRate ?? productDetails.commissionRate ?? 8
  );
  const commercialGross = Number(productDetails.price) || 0;
  const commercialCommission = Math.round(commercialGross * commercialRate) / 100;

  const [currentImage, setCurrentImage] = useState(null);
  const previewCanvasRef = useRef(null);
  const imgRef = useRef(null);

  const [aspect, setAspect] = useState(undefined);
  const [crop, setCrop] = useState({
    unit: "%",
    x: 0,
    y: 0,
    width: 90,
    height: 90,
  });
  const [completedCrop, setCompletedCrop] = useState(null);

  const ASPECT_RATIO = 1; // For 1:1 square crop
  const MIN_DIMENSION = 1000; // Minimum dimension requirement
  const PREVIEW_CONTAINER_SIZE = 300; // Preview container size

  const quillRef = useRef();

  useEffect(() => {
    if (!ownerId) return;
    dispatch(fetchOptionGroupsRestaurant(ownerId));
    dispatch(fetchOptionsRestaurants(ownerId));
  }, [dispatch, ownerId]);

  // If navigated without location.state (e.g. from admin menu panel), fetch product from API
  useEffect(() => {
    if (!location.state?.productDetails && productId) {
      apiFetchRestaurantProductDetail(productId)
        .then((res) => {
          const data = res.data?.data || res.data;
          if (data) {
            setProductDetails({ optionGroups: [], ...data });
            if (data.restaurantId) dispatch(fetchSections(data.restaurantId));
          }
        })
        .catch(() => toast.error("Failed to load product details"));
    } else if (location.state?.productDetails) {
      dispatch(fetchSections(location.state.productDetails.restaurantId || restaurantId));
    } else {
      dispatch(fetchSections(restaurantId));
    }
  }, [productId, location.state, dispatch, restaurantId]);

  useEffect(() => {
    if (Array.isArray(productDetails.optionGroups)) {
      const initialSelectedOptionGroups = productDetails.optionGroups.map(
        (optionGroup) => ({
          value: optionGroup._id,
          label: optionGroup.name,
        })
      );
      setSelectedOptionGroups(initialSelectedOptionGroups);
    }
  }, [productDetails.optionGroups]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "countInStock" && value < 1) {
      toast.error("Count in Stock cannot be less than 1");
      return;
    }

    setProductDetails({ ...productDetails, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: "" });
    }
  };

  const handleDescriptionChange = (content, delta, source, editor) => {
    // Update the state with the new content
    setProductDetails((prevDetails) => ({
      ...prevDetails,
      description: content,
    }));
  };

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ direction: "rtl" }],
      [{ color: [] }, { background: [] }],
      [{ font: ["montserrat"] }],
      [{ align: [] }],
      ["clean"],
      ["link", "image"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "script",
    "indent",
    "direction",
    "color",
    "background",
    "font",
    "align",
    "link",
    "image",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = { section: "", restaurant: "" };

    if (!productDetails.sectionId) {
      errors.section = "Section is required";
    }
    if (!productDetails.restaurantId) {
      errors.restaurant = "Restaurant is required";
    }

    if (errors.section || errors.restaurant) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    setUploading(true);
    let uploadedImageUrl = productDetails.image;

    if (
      uploadedImageUrl &&
      typeof uploadedImageUrl === "string" &&
      uploadedImageUrl.startsWith("data:")
    ) {
      uploadedImageUrl = await uploadImageToCloudinary(uploadedImageUrl);
      if (!uploadedImageUrl) {
        toast.error("Image upload failed, please try again.");
        setUploading(false);
        setSubmitting(false);
        return;
      }
    }

    /* A DISH WITH NO PHOTO IS A NORMAL STATE, NOT AN ERROR.
     *
     * This used to refuse the save outright. That contradicted a decision the
     * platform had already made and shipped: `image` on
     * restaurantSectionProductModel is optional with a "" default so photos can
     * be added progressively, the bulk menu importer creates photoless rows on
     * purpose (the 35-row Shaka Zulu upload is entirely such rows), and every
     * customer surface already falls back to the fork-and-plate placeholder.
     *
     * The block was also far wider than "add a photo". It gated the WHOLE form,
     * so on a photoless dish nobody could change the price, the stock, the
     * daily plate limit, the SEO tags, the option groups, or the discount
     * either. Worst of all it blocked DEACTIVATION: taking a dish off sale is
     * the one action that must never be refused, for the same reason
     * unpublishing a restaurant is never refused. An operator pulling something
     * down is not the moment to demand better photography.
     *
     * The nudge to add photos belongs where it already lives and costs nothing:
     * the "N no photo" filter chip in the menu manager. */

    /* Resolve newly-picked extra angles. The strip stores either
       https URLs (already saved on the server) or dataURLs (just
       picked) — the latter need an upload pass. The backend then
       diffs against the stored array and reclaims orphans. */
    let resolvedAdditional = [];
    const picked = Array.isArray(productDetails?.additionalImages)
      ? productDetails.additionalImages
      : [];
    for (const entry of picked) {
      if (typeof entry !== "string" || !entry) continue;
      if (entry.startsWith("data:")) {
        // eslint-disable-next-line no-await-in-loop
        const url = await uploadImageToCloudinary(entry, "product_angle");
        if (!url) {
          toast.error("One of the extra angles failed to upload.");
          setUploading(false);
          setSubmitting(false);
          return;
        }
        resolvedAdditional.push(url);
      } else {
        resolvedAdditional.push(entry);
      }
    }

    const updatedProductDetails = {
      ...productDetails,
      // Normalised to "" rather than left undefined: the model's empty-string
      // default is what every client's `Boolean(product.image)` placeholder
      // check reads, and undefined would drop the key from the update entirely.
      image: uploadedImageUrl || "",
      additionalImages: resolvedAdditional,
      // Vendor discount, in the API shape whatever the form state holds.
      discount: discountPayloadOf(productDetails.discount),
      /* `videos` rides in via the ...productDetails spread ONLY when the key
         exists in state — its absence must stay absence, so the server's
         "no videos key = leave stored videos alone" rule keeps working for
         stale navigations that predate the video feature. */
      optionGroups: selectedOptionGroups.map((group) => ({
        _id: group.value,
      })),
    };

    try {
      await dispatch(updateRestaurantProduct(productId, updatedProductDetails));
      // toast.success('Product updated successfully');
    } catch (error) {
      toast.error("Failed to update product");
    } finally {
      setSubmitting(false);
      history.push("/admin/restaurants");
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
      setUploadError("No file was selected.");
      toast.error("No file was selected.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        setImagePreview(reader.result);
        setProductDetails((prev) => ({ ...prev, image: reader.result }));
        setShowPreviewModal(true);

        const validFormats = ["image/jpeg", "image/png"];
        const isCurrentFormatCorrect = validFormats.includes(file.type);
        const isCurrentSizeCorrect = file.size <= 10 * 1024 * 1024;
        const isCurrentResolutionCorrect =
          img.width >= 1000 && img.height >= 1000;

        setIsFormatCorrect(isCurrentFormatCorrect);
        setIsSizeCorrect(isCurrentSizeCorrect);
        setIsResolutionCorrect(isCurrentResolutionCorrect);

        if (
          !isCurrentFormatCorrect ||
          !isCurrentSizeCorrect ||
          !isCurrentResolutionCorrect
        ) {
          let errorMessage =
            "Please ensure the image meets all the requirements:";
          if (!isCurrentFormatCorrect)
            errorMessage += " Must be JPG or PNG format.";
          if (!isCurrentSizeCorrect)
            errorMessage += " Must be less than or equal to 10MB.";
          if (!isCurrentResolutionCorrect)
            errorMessage += " Must be at least 1000x1000 pixels.";
          setUploadError(errorMessage);
        } else {
          setUploadError(null);
        }
      };
      img.onerror = () => {
        setUploadError("There was an error processing your image.");
        setImagePreview(null);
        setShowPreviewModal(false);
        toast.error("There was an error processing your image.");
      };
      img.src = reader.result;
    };
    reader.onerror = () => {
      setUploadError("Failed to read the image file.");
      setImagePreview(null);
      setProductDetails((prev) => ({ ...prev, image: null }));
      setShowPreviewModal(false);
    };
    reader.readAsDataURL(file);
  };

  const uploadImageToCloudinary = async (file, assetPurpose = "product_cover") => {
    if (!file) {
      toast.error("No image file selected!");
      return null;
    }

    setUploading(true);
    try {
      const result = await sharedUpload(file, {
        skipValidation: true,
        vendorMedia: {
          vendorType: "restaurant",
          vendorId: productDetails.restaurantId || restaurantId,
          assetPurpose,
          entityType: "restaurantProduct",
          entityId: productId,
          source: "dashboard",
        },
      });
      const secureUrl = result.secure_url;
      setProductDetails((prevProduct) => ({
        ...prevProduct,
        image: secureUrl,
      }));
      setUploading(false);
      setShowPreviewModal(false);
      return secureUrl;
    } catch (error) {
      console.error("Error uploading the image:", error);
      toast.error(`Failed to upload image: ${error.message}`);
      setUploading(false);
      setShowPreviewModal(false);
      return null;
    }
  };

  const openCreateGroup = () => {
    setEditingGroup(null);
    setShowOptionGroupModal(true);
  };
  const openEditGroup = (groupId) => {
    const found = (optionGroups || []).find((g) => String(g._id) === String(groupId));
    if (!found) return;
    setEditingGroup(found);
    setShowOptionGroupModal(true);
  };
  const closeOptionGroupModal = () => {
    setShowOptionGroupModal(false);
    setEditingGroup(null);
  };
  const groupLabel = (g) =>
    `${g.name} (Min: ${g.minOptions}, Max: ${g.maxOptions})`;
  /* After a create, attach the group to the dish: creating one left it
     unselected, so pressing Save Changes right afterwards wrote
     `optionGroups: []` and detached the group that had just been made.
     After an edit, refresh the chip's label so the rule it shows is current. */
  const handleGroupSaved = (saved) => {
    setSelectedOptionGroups((prev) => {
      const exists = prev.some((g) => String(g.value) === String(saved._id));
      const chip = { value: saved._id, label: groupLabel(saved) };
      return exists
        ? prev.map((g) => (String(g.value) === String(saved._id) ? chip : g))
        : [...prev, chip];
    });
  };

  const handleDeleteProduct = async () => {
    try {
      await dispatch(deleteRestaurantProduct(productId));
      toast.success("Product deleted successfully");
      history.push("/admin/restaurants");
    } catch (error) {
      toast.error("Failed to delete the product. Please try again.");
    }
  };

  function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
    return centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 90,
          height: 90,
        },
        aspect,
        mediaWidth,
        mediaHeight
      ),
      mediaWidth,
      mediaHeight
    );
  }

  const handleImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    let initialCrop;

    if (aspect) {
      initialCrop = centerAspectCrop(width, height, aspect);
    } else {
      initialCrop = {
        unit: "%",
        x: 10,
        y: 10,
        width: 80,
        height: 80,
      };
    }

    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  };

  const handleAspectChange = (newAspect) => {
    if (newAspect !== aspect) {
      setAspect(newAspect);
      if (imgRef.current) {
        const { width, height } = imgRef.current;
        setCrop(centerAspectCrop(width, height, newAspect));
      }
    } else {
      setAspect(undefined);
    }
  };

  const isCropValid = useCallback(() => {
    if (!completedCrop?.width || !completedCrop?.height) return false;

    const pixelWidth = imgRef.current
      ? completedCrop.width * (imgRef.current.naturalWidth / 100)
      : 0;
    const pixelHeight = imgRef.current
      ? completedCrop.height * (imgRef.current.naturalHeight / 100)
      : 0;

    if (aspect) {
      return pixelWidth >= MIN_DIMENSION && pixelHeight >= MIN_DIMENSION;
    }

    const MIN_FREE_CROP = 100;
    return pixelWidth >= MIN_FREE_CROP && pixelHeight >= MIN_FREE_CROP;
  }, [completedCrop, aspect]);

  const handleSaveCrop = useCallback(async () => {
    if (!imgRef.current || !completedCrop) return;

    const canvas = document.createElement("canvas");
    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const base64Image = canvas.toDataURL("image/jpeg", 1.0);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          console.error("Canvas is empty");
          return;
        }

        setProductDetails((prev) => ({
          ...prev,
          image: base64Image,
        }));
        setImagePreview(base64Image);
        setShowPreviewModal(false);
        setIsResolutionCorrect(true);
      },
      "image/jpeg",
      1
    );
  }, [completedCrop]);

  useEffect(() => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imgRef.current &&
      previewCanvasRef.current
    ) {
      const image = imgRef.current;
      const canvas = previewCanvasRef.current;
      const crop = completedCrop;

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext("2d");

      const pixelRatio = window.devicePixelRatio;

      canvas.width = crop.width * pixelRatio;
      canvas.height = crop.height * pixelRatio;

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width,
        crop.height
      );
    }
  }, [completedCrop]);

  return (
    <Container fluid className="vendor-product-form">
      <style>{customFontsCSS}</style>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

          .edit-product-card-adorned {
            animation: fadeIn 0.5s ease-out;
            border: none !important;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
            border-radius: 16px !important;
            overflow: hidden;
          }

          .edit-product-header-adorned {
            background: linear-gradient(135deg, #17385aff 0%, #0a578bff 100%) !important;
            color: white !important;
            padding: 32px !important;
            border: none !important;
            border-radius: 16px 16px 0 0 !important;
            position: relative;
          }

          .edit-product-body-adorned {
            padding: 32px !important;
          }

          .form-section-adorned {
            background: #f8f9fa;
            border-radius: 12px;
            padding: 24px;
            margin-bottom: 24px;
            border: 1px solid #e9ecef;
            transition: all 0.3s ease;
          }

          .form-section-adorned:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          }

          .form-label-adorned {
            font-family: 'Inter', sans-serif;
            font-weight: 600;
            font-size: 14px;
            color: #17385aff;
            margin-bottom: 8px;
          }

          .form-control-adorned {
            border-radius: 8px;
            border: 1px solid #e9ecef;
            padding: 10px 14px;
            font-family: 'Inter', sans-serif;
            transition: all 0.3s ease;
          }

          .form-control-adorned:focus {
            border-color: #17385aff;
            box-shadow: 0 0 0 3px rgba(23, 56, 90, 0.1);
          }

          .btn-adorned-primary {
            background: #17385aff !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-weight: 600 !important;
            font-family: 'Inter', sans-serif !important;
            transition: all 0.3s ease !important;
          }

          .btn-adorned-primary:hover {
            background: #0a578bff !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(23, 56, 90, 0.3) !important;
          }

          .btn-adorned-danger {
            background: #FB404B !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-weight: 600 !important;
            font-family: 'Inter', sans-serif !important;
            transition: all 0.3s ease !important;
          }

          .btn-adorned-danger:hover {
            background: #e63946 !important;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(251, 64, 75, 0.3) !important;
          }

          .btn-adorned-secondary {
            background: #6c757d !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-weight: 600 !important;
            font-family: 'Inter', sans-serif !important;
            transition: all 0.3s ease !important;
          }

          .btn-adorned-secondary:hover {
            background: #5a6268 !important;
            transform: translateY(-2px);
          }

          .info-badge-adorned {
            background: #e9ecef;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 24px;
            border-left: 4px solid #17385aff;
          }

          .info-badge-adorned strong {
            color: #17385aff;
            font-family: 'Inter', sans-serif;
          }

          .MuiIconButton-root {
              transition: all 0.2s ease-in-out;
          }
          
          .MuiIconButton-root:hover {
              transform: scale(1.1);
          }

          .image-action-button {
              border-radius: 20px;
              padding: 6px 16px;
              font-size: 0.875rem;
              text-transform: none;
              font-weight: 500;
              transition: all 0.2s ease-in-out;
              display: flex;
              align-items: center;
          }

          .image-action-button:hover {
              transform: translateY(-1px);
          }

          .modal-adorned .modal-content {
            border-radius: 16px !important;
            border: none !important;
            box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2) !important;
          }

          .modal-adorned .modal-header {
            background: #17385aff;
            color: white;
            border-radius: 16px 16px 0 0 !important;
            border: none !important;
            padding: 20px 24px !important;
          }

          .modal-adorned .modal-header .btn-close {
            filter: brightness(0) invert(1);
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
      `}
      </style>
      <Row className="justify-content-md-center">
        <Col md={10}>
          <Card className="mt-3 edit-product-card-adorned">
            <div className="edit-product-header-adorned">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <MdEdit size={36} style={{ marginRight: "16px" }} />
                  <div>
                    <h2 style={{ margin: 0, fontSize: "28px", fontWeight: "700", fontFamily: "'Inter', sans-serif" }}>
                      Edit Product
                    </h2>
                    <p style={{ margin: 0, opacity: 0.9, fontSize: "14px" }}>Update product details and settings</p>
                  </div>
                </div>
                <div className="d-flex" style={{ gap: 8 }}>
                  {AUDIT_ENABLED && (
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={() => setShowHistory(true)}
                      title="View every change ever made to this product"
                    >
                      <i className="fa fa-history" style={{ marginRight: 6 }} /> Version History
                    </Button>
                  )}
                  <Button
                    className="btn-adorned-danger"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <FaTrash size={16} style={{ marginRight: "8px" }} />
                    Delete Product
                  </Button>
                </div>
              </div>
            </div>
            <Card.Body className="edit-product-body-adorned">
              <div className="info-badge-adorned">
                <div style={{ marginBottom: "8px", fontSize: "12px" }}>
                  <strong>Product ID: </strong>
                  <span style={{ fontFamily: "monospace", color: "#6c757d" }}>{productDetails._id}</span>
                </div>
                <div style={{ marginBottom: "8px", fontSize: "12px" }}>
                  <strong>Created: </strong>
                  {productDetails.createdAt && !isNaN(new Date(productDetails.createdAt))
                    ? format(new Date(productDetails.createdAt), "MMMM dd, yyyy h:mm a")
                    : "—"}
                </div>
                <div style={{ marginBottom: "0", fontSize: "12px" }}>
                  <strong>Updated: </strong>
                  {productDetails.updatedAt && !isNaN(new Date(productDetails.updatedAt))
                    ? format(new Date(productDetails.updatedAt), "MMMM dd, yyyy h:mm a")
                    : "—"}
                </div>
              </div>
              <Form onSubmit={handleSubmit}>
                {/* Vendor team permissions: what this login may change here. */}
                {gate.pricingOnly && (
                  <Alert variant="info" className="mb-3">
                    Your role allows price changes only. Every other field is locked; Save will update the pricing fields.
                  </Alert>
                )}
                {gate.readOnly && (
                  <Alert variant="warning" className="mb-3">
                    {gate.readOnlyMessage}
                  </Alert>
                )}

                <Form.Group className="mb-1 mt-2">
                  <Form.Label>Product Image</Form.Label>
                  <Row>
                    <Col md={6}>
                      <Card style={{ width: "100%" }}>
                        <Card.Body
                          className="d-flex flex-column align-items-center justify-content-center"
                          style={{ minHeight: "220px" }}
                        >
                          {productDetails.image ? (
                            <img
                              src={productDetails.image}
                              alt="Product"
                              style={{ maxHeight: "220px", maxWidth: "100%" }}
                            />
                          ) : (
                            <Card.Text>
                              <Button
                                variant="primary"
                                as="label"
                                className="d-flex align-items-center justify-content-center"
                                style={{ color: "#ffff" }}
                              >
                                <MdAddAPhoto size={24} className="me-2" />
                                Add Image
                                <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                                  type="file"
                                  hidden
                                  accept=".jpg, .png, .jpeg"
                                  onChange={handleImageChange}
                                  id="file-input"
                                />
                              </Button>
                            </Card.Text>
                          )}
                        </Card.Body>
                      </Card>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginLeft: "45px",
                        }}
                      >
                        <Button
                          variant="link"
                          as="label"
                          style={{
                            color: "green",
                            textDecoration: "none",
                            textTransform: "capitalize",
                            fontSize: "12px",
                          }}
                        >
                          Replace Image
                          <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                            type="file"
                            hidden
                            accept=".jpg, .png, .jpeg"
                            onChange={handleImageChange}
                            id="file-input"
                          />
                        </Button>
                        <span
                          style={{
                            borderLeft: "1px solid #ccc",
                            height: "40px",
                          }}
                        ></span>
                        <Button
                          variant="link"
                          style={{
                            color: "red",
                            textDecoration: "none",
                            fontSize: "12px",
                          }}
                          onClick={handleDeleteCover}
                        >
                          Delete Image
                        </Button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <Card.Text
                        as="div"
                        className="text-muted"
                        style={{ fontSize: "12px" }}
                      >
                        <p style={{ marginBottom: "0" }}>
                          <strong>File requirements: </strong>
                        </p>
                        <p style={{ fontStyle: "italic", fontSize: "11px" }}>
                          <FaInfoCircle style={{ marginRight: "5px" }} />
                          {productPhotoHint()}
                        </p>
                        {/* The artwork template for this slot, when one is configured. */}
                        <ArtworkTemplateLink slotKey="restaurant.productCover" variant="inline" />
                        <p style={{ fontSize: "12px" }}>
                          <FaCameraRetro style={{ marginRight: "5px" }} />
                          Ensure your product is in focus and make all the
                          ingredients visible.
                        </p>
                        <p style={{ fontSize: "12px" }}>
                          <FaSquareFull style={{ marginRight: "5px" }} />
                          Show the full product and place it at the centre of
                          the frame.
                        </p>
                        <p style={{ fontSize: "12px" }}>
                          <FaSpellCheck style={{ marginRight: "5px" }} />
                          Remove distractions and avoid using text.
                        </p>
                        <p style={{ fontSize: "12px" }}>
                          <FaStar style={{ marginRight: "5px" }} />
                          Use a clear background and find bright lighting.
                        </p>
                        <MobileProductShootPicker
                          vendorId={ownerId || restaurantId}
                          disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                          onSelectImage={(imageUrl) => {
                            setProductDetails((prev) => ({
                              ...prev,
                              image: imageUrl,
                            }));
                            setImagePreview(imageUrl);
                            setUploadError(null);
                          }}
                        />
                        <AdditionalImagesStrip
                          value={productDetails?.additionalImages || []}
                          onChange={(next) =>
                            setProductDetails((prev) => ({
                              ...prev,
                              additionalImages: next,
                            }))
                          }
                          onRequestDeleteSaved={setPhotoDeleteTarget}
                          disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                        />
                      </Card.Text>
                    </Col>
                  </Row>
                </Form.Group>

                <ProductVideoSection
                  videos={productDetails?.videos || []}
                  onChange={(next) =>
                    setProductDetails((prev) => ({ ...prev, videos: next }))
                  }
                  disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                />

                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                    type="text"
                    placeholder="Enter product name"
                    name="name"
                    value={productDetails.name || ""}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <div className="vendor-description">
                    <ReactQuill readOnly={gate.lockCatalogue}
                      ref={quillRef}
                      value={productDetails.description}
                      onChange={handleDescriptionChange}
                      modules={modules}
                      formats={formats}
                      className="vendor-description-editor"
                      onPaste={handlePaste}
                    // defaultValue="<p><span class='ql-size-small'>Start typing here...</span></p>"
                    />
                  </div>
                </Form.Group>

                <hr style={{ margin: "28px 0" }} />

                <Form.Group className="mb-3">
                  <Form.Label>Section</Form.Label>
                  <AppSelect block disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                    name="sectionId"
                    value={productDetails?.sectionId || ""}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Choose...</option>
                    {sections.map((section) => (
                      <option key={section._id} value={section._id}>
                        {section.title}
                      </option>
                    ))}
                  </AppSelect>
                  {formErrors.section && (
                    <Alert variant="danger">{formErrors.section}</Alert>
                  )}
                </Form.Group>

                <WorkingRestaurantField
                  restaurant={workingRestaurant}
                  loading={workingRestaurantLoading}
                />
                {formErrors.restaurant && (
                  <Alert variant="danger">{formErrors.restaurant}</Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Price</Form.Label>
                  <Form.Control disabled={gate.lockPricing} title={gate.pricingTitle}
                    type="number"
                    placeholder="Enter product price"
                    name="price"
                    value={productDetails.price || 0}
                    onChange={handleInputChange}
                    required
                  />
                  <Alert variant="info" className="mt-2 mb-0">
                    {commercialRestaurant?.commissionConfigured ? "Contract" : "Fallback (needs commercial review)"} {commercialRate}% · Lusuku commission UGX {commercialCommission.toLocaleString()} · Restaurant net UGX {(commercialGross - commercialCommission).toLocaleString()}
                  </Alert>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Customer payment requirement</Form.Label>
                  <AppSelect block
                    name="requiredPaymentMethod"
                    value={productDetails.requiredPaymentMethod || "NONE"}
                    onChange={handleInputChange}
                    disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                  >
                    <option value="NONE">Any method (Cash on Delivery allowed)</option>
                    <option value="ANY_ONLINE">Pay before acceptance (any online method)</option>
                    <option value="MOMO">MTN Mobile Money only</option>
                    <option value="AIRTEL">Airtel Money only</option>
                    <option value="VISA">Card only</option>
                    <option value="ZAMTEL">Zamtel Kwacha only</option>
                  </AppSelect>
                  <Form.Text className="text-muted">
                    Prepaid orders stay locked until Lusuku confirms payment.
                  </Form.Text>
                </Form.Group>
                {/* Vendor discount: the list price above stays; the sale price is derived on read. */}
                <DiscountFields
                  disabled={gate.lockPricing}
                  title={gate.pricingTitle}
                  value={productDetails.discount}
                  onChange={(next) => setProductDetails((prev) => ({ ...prev, discount: next }))}
                  listPrice={productDetails.price}
                />

                <Form.Group className="mb-3">
                  <Form.Label>Count In Stock</Form.Label>
                  <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                    type="number"
                    placeholder="Enter count in stock"
                    name="countInStock"
                    value={productDetails.countInStock || 1}
                    onChange={handleInputChange}
                    min="1"
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Daily plate limit</Form.Label>
                  <Form.Control disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                    type="number"
                    placeholder="Leave empty for no daily limit"
                    name="dailyLimit"
                    value={productDetails.dailyLimit ?? ""}
                    onChange={handleInputChange}
                    min="1"
                    step="1"
                  />
                  <Form.Text className="text-muted">
                    How many plates the kitchen makes per day. Once that many have been
                    ordered on any channel (counter, app, web, WhatsApp) the dish shows as
                    sold out for today everywhere and comes back on its own tomorrow.
                    Empty means no daily limit.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    SEO Tags{" "}
                    <small className="text-muted" style={{ fontWeight: "normal", fontSize: "11px" }}>
                      — helps search engines find this product. Press Enter or comma to add.
                    </small>
                  </Form.Label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "6px 10px", border: "1px solid #ced4da", borderRadius: 4, minHeight: 40, background: "#fff", cursor: "text" }}
                    onClick={(e) => e.currentTarget.querySelector("input")?.focus()}>
                    {(productDetails.tags || []).map((tag, i) => (
                      <span key={i} style={{ display: "inline-flex", alignItems: "center", background: "#e8f4fd", color: "#1a56db", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600, gap: 4 }}>
                        {tag}
                        <span onClick={() => setProductDetails(prev => ({ ...prev, tags: (prev.tags || []).filter((_, idx) => idx !== i) }))}
                          style={{ cursor: "pointer", marginLeft: 2, fontWeight: 700, color: "#888" }}>×</span>
                      </span>
                    ))}
                    <input
                      type="text"
                      placeholder={(productDetails.tags || []).length === 0 ? `e.g. ${productDetails.name ? productDetails.name.split(" ")[0].toLowerCase() : "burger"}, spicy, fast-food` : ""}
                      style={{ border: "none", outline: "none", fontSize: 13, minWidth: 140, flex: 1 }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                          e.preventDefault();
                          const val = e.target.value.trim().replace(/,$/, "");
                          if (val && !(productDetails.tags || []).includes(val)) {
                            setProductDetails(prev => ({ ...prev, tags: [...(prev.tags || []), val] }));
                          }
                          e.target.value = "";
                        }
                      }}
                      onBlur={(e) => {
                        const val = e.target.value.trim();
                        if (val && !(productDetails.tags || []).includes(val)) {
                          setProductDetails(prev => ({ ...prev, tags: [...(prev.tags || []), val] }));
                          e.target.value = "";
                        }
                      }}
                    />
                  </div>
                  <Form.Text className="text-muted" style={{ fontSize: 11 }}>
                    If left empty, the product name will be used as the default tag for SEO.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Option Groups</Form.Label>
                  <Select isDisabled={gate.lockCatalogue}
                    isMulti
                    options={optionGroups.map((optionGroup) => ({
                      value: optionGroup._id,
                      label: `${optionGroup.name} (Min: ${optionGroup.minOptions}, Max: ${optionGroup.maxOptions})`,
                    }))}
                    value={selectedOptionGroups}
                    onChange={setSelectedOptionGroups}
                    placeholder="Select option groups..."
                  />
                  {selectedOptionGroups.length > 0 ? (
                    <div className="mt-2">
                      {selectedOptionGroups.map((chip) => {
                        const g = (optionGroups || []).find(
                          (x) => String(x._id) === String(chip.value)
                        );
                        if (!g) return null;
                        const max = Number(g.maxOptions);
                        const rule =
                          max === 1
                            ? "pick one"
                            : max > 1
                              ? `up to ${max}`
                              : "any number";
                        return (
                          <div
                            key={chip.value}
                            className="d-flex align-items-center justify-content-between py-1"
                            style={{ borderBottom: "1px solid #eef2f6", fontSize: 13 }}
                          >
                            <span>
                              <strong>{g.name}</strong>
                              <span className="text-muted">
                                {" "}
                                {Number(g.minOptions) > 0 ? "required" : "optional"}, {rule},{" "}
                                {(g.options || []).length} option{(g.options || []).length === 1 ? "" : "s"}
                              </span>
                            </span>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() => openEditGroup(chip.value)}
                            >
                              <MdEdit style={{ marginRight: 4 }} />
                              Edit
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  <Button variant="link" onClick={openCreateGroup}>
                    Create New Option Group
                  </Button>
                </Form.Group>

                <Form.Group className="mb-3">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={productDetails.enabled ?? true}
                        onChange={(e) =>
                          setProductDetails({
                            ...productDetails,
                            enabled: e.target.checked,
                          })
                        }
                        name="enabled"
                        color="secondary"
                        disabled={gate.lockActivation} title={gate.activationTitle}
                      />
                    }
                    label={
                      productDetails.enabled
                        ? "Activate Product For Customers"
                        : "Deactivate Product For Customers"
                    }
                    classes={{ label: classes.label }}
                  />
                </Form.Group>

                <Button
                  className="btn-adorned-secondary"
                  style={{
                    marginRight: "10px",
                  }}
                  onClick={() => history.goBack()}
                  disabled={gate.lockCatalogue} title={gate.catalogueTitle}
                >
                  Discard Changes
                </Button>

                <Button
                  className="btn-adorned-primary"
                  type="submit"
                  disabled={gate.lockSubmit} title={gate.submitTitle}
                >
                  {submitting ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        size="lg"
        centered
        className="modal-adorned"
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center justify-content-between w-100">
            <span>Crop and Preview Image</span>
            <div className="d-flex gap-2">
              <Tooltip title="Free Crop">
                <IconButton
                  size="small"
                  onClick={() => handleAspectChange(undefined)}
                  style={{
                    backgroundColor: !aspect
                      ? "rgba(25, 118, 210, 0.12)"
                      : "transparent",
                    color: !aspect ? "#1976d2" : "rgba(0, 0, 0, 0.54)",
                  }}
                >
                  <MdCrop size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Square (1:1)">
                <IconButton
                  size="small"
                  onClick={() => handleAspectChange(1)}
                  style={{
                    backgroundColor:
                      aspect === 1 ? "rgba(25, 118, 210, 0.12)" : "transparent",
                    color: aspect === 1 ? "#1976d2" : "rgba(0, 0, 0, 0.54)",
                  }}
                >
                  <MdCropSquare size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Landscape (16:9)">
                <IconButton
                  size="small"
                  onClick={() => handleAspectChange(16 / 9)}
                  style={{
                    backgroundColor:
                      aspect === 16 / 9
                        ? "rgba(25, 118, 210, 0.12)"
                        : "transparent",
                    color:
                      aspect === 16 / 9 ? "#1976d2" : "rgba(0, 0, 0, 0.54)",
                  }}
                >
                  <MdCropLandscape size={20} />
                </IconButton>
              </Tooltip>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex">
            <div
              className="flex-grow-1"
              style={{
                maxHeight: "70vh",
                overflow: "auto",
                backgroundColor: "#f0f0f0",
                borderRadius: "4px",
                padding: "16px",
              }}
            >
              {imagePreview && (
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={aspect}
                  minWidth={aspect ? MIN_DIMENSION : undefined}
                  minHeight={aspect ? MIN_DIMENSION : undefined}
                >
                  <img
                    ref={imgRef}
                    alt="Crop me"
                    src={imagePreview}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "70vh",
                      objectFit: "contain",
                    }}
                    onLoad={handleImageLoad}
                  />
                </ReactCrop>
              )}
            </div>
            <div
              className="ms-3"
              style={{
                width: "280px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <h6>Preview</h6>
              <div
                style={{
                  width: "100%",
                  aspectRatio: "1",
                  overflow: "hidden",
                  backgroundColor: "#f0f0f0",
                  borderRadius: "4px",
                }}
              >
                <canvas
                  ref={previewCanvasRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </div>
              <div className="mt-2">
                <Form.Text className="text-muted">
                  <strong>Image Requirements:</strong>
                  <ul style={{ fontSize: "12px", marginTop: "8px" }}>
                    <li>
                      Format: JPG or PNG
                      {isFormatCorrect ? (
                        <span className="text-success"> ✔</span>
                      ) : (
                        <span className="text-danger"> ✘</span>
                      )}
                    </li>
                    <li>
                      Size: ≤ 10MB
                      {isSizeCorrect ? (
                        <span className="text-success"> ✔</span>
                      ) : (
                        <span className="text-danger"> ✘</span>
                      )}
                    </li>
                    <li>
                      Dimensions: ≥ 1000x1000px
                      {completedCrop &&
                        completedCrop.width >= MIN_DIMENSION &&
                        completedCrop.height >= MIN_DIMENSION ? (
                        <span className="text-success"> ✔</span>
                      ) : (
                        <span className="text-danger"> ✘</span>
                      )}
                    </li>
                  </ul>
                </Form.Text>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-adorned-secondary"
            onClick={() => {
              setShowPreviewModal(false);
              setCrop({
                unit: "%",
                x: 0,
                y: 0,
                width: 90,
                height: 90,
              });
              setCompletedCrop(null);
            }}
          >
            Cancel
          </Button>
          <Button
            className="btn-adorned-primary"
            onClick={handleSaveCrop}
            disabled={!isCropValid()}
          >
            Save Cropped Image
          </Button>
        </Modal.Footer>
      </Modal>

      <OptionGroupModal
        show={showOptionGroupModal}
        onClose={closeOptionGroupModal}
        ownerId={ownerId || restaurantId}
        productId={productId}
        group={editingGroup}
        onSaved={handleGroupSaved}
      />

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} className="modal-adorned">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this product? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="btn-adorned-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="btn-adorned-danger"
            onClick={handleDeleteProduct}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {AUDIT_ENABLED && (
        <VersionHistoryDrawer
          show={showHistory}
          onHide={() => setShowHistory(false)}
          entityType="RestaurantProduct"
          entityId={productDetails?._id}
          entityLabel={productDetails?.name}
        />
      )}

      <ProductMediaDeleteDialog
        target={photoDeleteTarget}
        productType="restaurant"
        productId={productId}
        productName={productDetails?.name}
        image={productDetails?.image}
        additionalImages={productDetails?.additionalImages}
        onClose={() => setPhotoDeleteTarget(null)}
        onRemoved={handlePhotosRemoved}
      />
    </Container>
  );
};

export default EditMenuProductForm;
